import axios from 'axios'
import { setupMobileInterceptors } from './mobileAuthService.js'
import { USE_JWT_COOKIE } from '../../shared/auth/authConfig.js'

/**
 * APIベースURL
 * モバイル撮影用のAPIエンドポイント
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pix/api'

/**
 * モバイル撮影用のaxiosインスタンス
 * 共通の設定とインターセプターが適用されたHTTPクライアント
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_JWT_COOKIE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// モバイル撮影用のインターセプターを設定
setupMobileInterceptors(apiClient, 'Mobile App Satsuei')

/**
 * 初期表示用データ取得
 * 撮影画面の初期表示に必要なデータを取得
 * @param {Object} params - パラメータ（未使用）
 * @returns {Promise<Object>} 初期表示データ
 */
export const getInitialData = async () => {
  const response = await apiClient.post('/appSatsuei', {})
  return response.data
}

/**
 * 撮影メモカテゴリー取得
 * 撮影メモのカテゴリー一覧を取得
 * @returns {Promise<Object>} カテゴリー一覧データ
 */
export const getSatsueiMemoCategory = async () => {
  const response = await apiClient.post('/getSatsueiMemoCategory', {})
  return response.data
}

/**
 * 画像フォルダーリスト取得
 * 画像保存用のフォルダー一覧を取得
 * @returns {Promise<Object>} フォルダー一覧データ
 */
export const getImageFolderList = async () => {
  const response = await apiClient.post('/getImageFolderList', {})
  return response.data
}

/**
 * 撮影ガイドリスト取得
 * 指定されたカテゴリーの撮影ガイド一覧を取得
 * @param {string} satsueiMemoCategoryId - 撮影メモカテゴリーID
 * @returns {Promise<Object>} 撮影ガイド一覧データ
 */
export const getSatsueiGuideList = async (satsueiMemoCategoryId) => {
  const response = await apiClient.post('/getSatsueiGuideList', {
    satsueiMemoCategoryId: satsueiMemoCategoryId ?? ''
  })
  return response.data
}

/**
 * 撮影ガイド詳細リスト取得
 * 指定された撮影ガイドの詳細情報を取得
 * @param {string} satsueiMemoCategoryId - 撮影メモカテゴリーID
 * @param {string} satsueiGuideId - 撮影ガイドID
 * @returns {Promise<Object>} 撮影ガイド詳細データ
 */
export const getSatsueiGuideDetailList = async (satsueiMemoCategoryId, satsueiGuideId) => {
  const response = await apiClient.post('/getSatsueiGuideDetailList', {
    satsueiMemoCategoryId: satsueiMemoCategoryId,
    satsueiGuideId: satsueiGuideId
  })
  return response.data
}

/**
 * オブジェクト数取得
 * 指定されたオブジェクトの関連データ数を取得
 * @param {string} objectId - オブジェクトID
 * @returns {Promise<Object>} オブジェクト数データ
 */
export const getObjectCount = async (objectId) => {
  const response = await apiClient.post('/getObjectCount', {
    objectId: objectId
  })
  return response.data
}

/**
 * 最大オブジェクトID取得
 * 新しいオブジェクト用の最大IDを取得
 * @returns {Promise<Object>} 最大オブジェクトIDデータ
 */
export const getMaxObjectId = async () => {
  const response = await apiClient.post('/getMaxObjectId', {})
  return response.data
}

/**
 * オブジェクト登録
 * 新しいオブジェクトをシステムに登録
 * @param {string} objectId - オブジェクトID
 * @param {string} satsueiMemoCategoryId - 撮影メモカテゴリーID
 * @param {string} kyotenId - 拠点ID
 * @param {string} satsueiGuideId - 撮影ガイドID
 * @returns {Promise<Object>} 登録結果
 */
export const registObject = async (objectId, satsueiMemoCategoryId, kyotenId, satsueiGuideId) => {
  const response = await apiClient.post('/addObject', {
    objectId: objectId,
    satsueiMemoCategoryId: satsueiMemoCategoryId,
    kyotenId: kyotenId,
    satsueiGuideId: satsueiGuideId
  })
  return response.data
}

/**
 * カメラ起動用のデータ準備
 * ネイティブカメラアプリ起動に必要なデータを準備
 * @param {Object} params - カメラ起動パラメータ
 * @param {string} params.objectId - オブジェクトID
 * @param {string} params.satsueiMemoCategoryId - 撮影メモカテゴリーID
 * @param {string} params.userKyotenId - ユーザー拠点ID
 * @param {number} params.imageFolderId - 画像フォルダーID
 * @param {string} params.satsueiGuideId - 撮影ガイドID
 * @param {string} params.backUrl - 戻り先URL
 * @param {boolean} params.isSaveLocal - ローカル保存フラグ
 * @param {string} params.target - ターゲット（デフォルト: 'new'）
 * @param {number} params.startImageOrderIndex - 開始画像順序インデックス（デフォルト: 0）
 * @param {number} params.startMovieOrderIndex - 開始動画順序インデックス（デフォルト: 0）
 * @returns {Promise<Object>} カメラ起動用データ
 */
export const prepareCameraData = async (params) => {
  const {
    objectId,
    satsueiMemoCategoryId,
    userKyotenId,
    imageFolderId,
    satsueiGuideId,
    backUrl,
    isSaveLocal,
    target = 'new',
    startImageOrderIndex = 0,
    startMovieOrderIndex = 0,
    cameraType = 'default',
    thetaCameraUsage = 0
  } = params

  // バックエンド（RegistSatsueiKanryoModel）の型に合わせて統一
  const jsonData = {
    objectId: String(objectId ?? ''),                                    // String
    satsueiMemoCategoryId: parseInt(satsueiMemoCategoryId, 10) || 0,    // Integer
    kyotenId: parseInt(userKyotenId, 10) || null,                       // Integer (nullable)
    imageFolderId: parseInt(imageFolderId, 10) || 0,                     // Integer
    satsueiGuideId: parseInt(satsueiGuideId, 10) || null,               // Integer (nullable)
    satsueiGuideDetailList: [],
    backUrl: String(backUrl ?? ''),                                     // String
    isSaveLocal: String(isSaveLocal ?? ''),                             // String
    target: String(target ?? 'new'),                                    // String
    startImageOrderIndex: parseInt(startImageOrderIndex, 10) || 0,      // Integer
    startMovieOrderIndex: parseInt(startMovieOrderIndex, 10) || 0,      // Integer
    cameraType: String(cameraType ?? 'default'),                        // String
    thetaCameraUsage: parseInt(thetaCameraUsage, 10) || 0,              // Integer
    isImageResize: parseInt(params.isImageResize ?? 1, 10),             // Integer (0=リサイズなし, 1=リサイズ)
    displayStr: String(objectId ?? '')                                   // String
  }

  // 撮影ガイド詳細リストを取得
  if(satsueiMemoCategoryId && satsueiGuideId){
    const guideDetailData = await getSatsueiGuideDetailList(satsueiMemoCategoryId, satsueiGuideId)
    
    // 撮影ガイド詳細リストを設定
    if (guideDetailData.satsueiGuideDetailList) {
      jsonData.satsueiGuideDetailList = []
      for (let i = 0; i < guideDetailData.satsueiGuideDetailList.length; i++) {
        const detail = guideDetailData.satsueiGuideDetailList[i]
        const satsueiGuideImageUrl = `${API_BASE_URL}/getImage?` +
          `type=satsueiGuideImage&` +
          `satsueiMemoCategoryId=${satsueiMemoCategoryId}&` +
          `filename=${satsueiGuideId}_${detail.satsueiGuideDetailId}.${detail.filenameExtension}`
      
        jsonData.satsueiGuideDetailList[i] = {
          satsueiGuideDetailId: detail.satsueiGuideDetailId,
          orderIndex: detail.orderIndex,
          satsueiGuideDetailName: detail.satsueiGuideDetailName,
          setsumei: detail.setsumei,
          satsueiGuideImageUrl: satsueiGuideImageUrl
        }
      }
   }
  }

  return jsonData
}

/**
 * ネイティブカメラアプリ起動
 * iOS/Androidのネイティブカメラアプリを起動
 * @param {Object} jsonData - カメラ起動用データ
 */
export const startUpCamera = (jsonData) => {
  const jsonStr = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData)
  console.log('[startUpCamera] params:', jsonData)
  // iOS (WKWebView)
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.startUpCamera) {
    window.webkit.messageHandlers.startUpCamera.postMessage(jsonStr)
  }
  // Android (Flutter WebView JavaScriptChannel)
  else if (window.startUpCamera && window.startUpCamera.postMessage) {
    window.startUpCamera.postMessage(jsonStr)
  }
  else {
    console.warn('Native camera interface not available')
  }
}

/**
 * ネイティブコードリーダーアプリ起動
 * iOS/Androidのネイティブコードリーダーアプリを起動
 * @param {Object} jsonData - コードリーダー起動用データ
 */
export const startUpCodeReader = (jsonData) => {
  const jsonStr = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData)
  console.log('[startUpCodeReader] params:', jsonData)
  // iOS (WKWebView)
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.startUpCodeReader) {
    window.webkit.messageHandlers.startUpCodeReader.postMessage(jsonStr)
  }
  // Android (Flutter WebView JavaScriptChannel)
  else if (window.startUpCodeReader && window.startUpCodeReader.postMessage) {
    window.startUpCodeReader.postMessage(jsonStr)
  }
  else {
    console.warn('Native code reader interface not available')
  }
}

/**
 * バーコード読み取り完了時のコールバック
 * バーコードスキャン結果を処理し、適切なアクションを決定
 * @param {Object} jsonData - バーコードスキャン結果データ
 * @param {string} jsonData.objectId - オブジェクトID
 * @param {string} jsonData.backUrl - 戻り先URL
 * @returns {Promise<Object>} 処理結果（アクション、オブジェクトID、メッセージ/データ）
 */
export const handleCodeScanResult = async (jsonData) => {
  const objectId = jsonData.objectId
  
  // オブジェクト数を確認
  const objectCountData = await getObjectCount(objectId)
  const objectCount = objectCountData.objectCount
  
  if (objectCount >= 1) {
    // 撮影済みIDなので詳細ページに移動
    return {
      action: 'showDetail',
      objectId: objectId,
      message: '撮影済みIDなので詳細ページに移動します。'
    }
  } else {
    // 新規撮影
    return {
      action: 'startCamera',
      objectId: objectId,
      data: jsonData
    }
  }
}

/**
 * モバイル撮影サービス
 * 撮影画面の初期化、カメラ起動、バーコードスキャンなどの撮影関連機能を提供
 */
export const mobileSatsueiService = {
  // === データ取得 ===
  getInitialData,              // 初期表示用データ取得
  getSatsueiMemoCategory,      // 撮影メモカテゴリー取得
  getImageFolderList,          // 画像フォルダーリスト取得
  getSatsueiGuideList,         // 撮影ガイドリスト取得
  getSatsueiGuideDetailList,   // 撮影ガイド詳細リスト取得
  getObjectCount,              // オブジェクト数取得
  getMaxObjectId,              // 最大オブジェクトID取得

  // === オブジェクト操作 ===
  registObject,                // オブジェクト登録

  // === カメラ・撮影機能 ===
  prepareCameraData,           // カメラ起動用データ準備
  startUpCamera,               // ネイティブカメラ起動
  startUpCodeReader,            // ネイティブコードリーダー起動
  handleCodeScanResult,         // バーコードスキャン結果処理
}
